/**
 * Events Testing API Contracts
 *
 * Shared schemas/types for the admin /internal/events/emit-test endpoint.
 * Used by the backend handler (validation) and the web admin testing tool
 * (request shape + dropdown labels).
 */

import { z } from 'zod'

/**
 * Supported test event types that can be emitted for testing
 */
export const TestEventTypeSchema = z.enum(['ASSIGNMENT_CREATED', 'MEMBER_JOINED_GROUP', 'MEMBER_LEFT_GROUP'])

export type TestEventType = z.infer<typeof TestEventTypeSchema>

/**
 * Human-readable labels for each test event type. Used by the admin UI
 * dropdown so the label set stays in sync with the enum.
 */
export const TEST_EVENT_LABELS: Record<TestEventType, string> = {
  ASSIGNMENT_CREATED: 'Assignment Created',
  MEMBER_JOINED_GROUP: 'Member Joined Group',
  MEMBER_LEFT_GROUP: 'Member Left Group'
}

/**
 * Request body schema for POST /internal/events/emit-test
 */
export const EmitTestEventRequestSchema = z.object({
  eventType: TestEventTypeSchema,
  schoolId: z.string().min(1),
  recipientUserId: z.string().min(1),
  actorUserId: z.string().optional(),
  payload: z.record(z.string(), z.unknown()).optional()
})

export type EmitTestEventRequest = z.infer<typeof EmitTestEventRequestSchema>

/**
 * Successful response payload for POST /internal/events/emit-test
 */
export interface EmitTestEventResult {
  /** Generated event ID for tracking (used as correlationId in logs) */
  eventId: string
  /** Type of event that was emitted */
  eventType: TestEventType
  /** User who will receive the notification */
  recipientUserId: string
}
